import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Plus,
  Send,
  FileText,
  Lightbulb,
  Users,
  Lock,
  Building2,
  MoreHorizontal,
  Edit3,
  Trash2,
  Upload,
  X,
  MessageSquare,
  Clock,
  Star,
  Settings,
  Share2,
  ChevronDown,
  FolderOpen,
  BarChart3,
  TrendingUp,
  Newspaper,
  PenLine,
  Briefcase,
  Code,
  Globe,
  Zap,
  Target,
  Search,
  BookOpen,
  Layers,
  Database
} from 'lucide-react';
import { useProjectStore, MOCK_PROJECTS } from '../../../stores/projectStore';
import { DEPARTMENTS } from '../../../stores/teamStore';

// 아이콘 매핑
const ICON_MAP = {
  folder: FolderOpen,
  chart: BarChart3,
  trend: TrendingUp,
  news: Newspaper,
  pen: PenLine,
  business: Briefcase,
  code: Code,
  globe: Globe,
  zap: Zap,
  target: Target,
  search: Search,
  chat: MessageSquare,
  book: BookOpen,
  layers: Layers,
  database: Database,
  settings: Settings,
};

const ICON_COLORS = {
  folder: 'text-blue-400',
  chart: 'text-emerald-400',
  trend: 'text-green-400',
  news: 'text-amber-400',
  pen: 'text-orange-400',
  business: 'text-slate-400',
  code: 'text-violet-400',
  globe: 'text-cyan-400',
  zap: 'text-yellow-400',
  target: 'text-red-400',
  search: 'text-indigo-400',
  chat: 'text-pink-400',
  book: 'text-teal-400',
  layers: 'text-purple-400',
  database: 'text-sky-400',
  settings: 'text-gray-400',
};

// 대화 항목 컴포넌트
const ConversationItem = ({ conversation, isActive, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 rounded-xl transition-colors ${
        isActive
          ? 'bg-bg-300/50'
          : 'hover:bg-bg-200'
      }`}
    >
      <div className="font-medium text-text-100 text-sm">
        About {conversation.title}
      </div>
      <div className="text-xs text-text-500 mt-1">
        Last message {conversation.lastMessage}
      </div>
    </button>
  );
};

// 메인 컴포넌트
const ProjectDetail = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { projects, updateProject } = useProjectStore();

  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [activeConversationId, setActiveConversationId] = useState(null);

  // 편집 상태
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [isEditingInstructions, setIsEditingInstructions] = useState(false);
  const [editInstructions, setEditInstructions] = useState('');

  // 프로젝트 로드
  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      let found = projects.find(p => p.id === projectId);
      if (!found) {
        found = MOCK_PROJECTS.find(p => p.id === projectId);
      }
      if (found) {
        setProject(found);
        setEditName(found.name || '');
        setEditInstructions(found.instructions || '');
        if (found.conversations?.length > 0) {
          setActiveConversationId(found.conversations[0].id);
        }
      }
      setIsLoading(false);
    }, 300);
  }, [projectId, projects]);

  const handleSaveName = () => {
    if (project && editName.trim()) {
      updateProject(project.id, { name: editName.trim() });
      setProject({ ...project, name: editName.trim() });
    }
    setIsEditingName(false);
  };

  const handleSaveInstructions = () => {
    if (project) {
      updateProject(project.id, { instructions: editInstructions });
      setProject({ ...project, instructions: editInstructions });
    }
    setIsEditingInstructions(false);
  };

  const handleNewConversation = () => {
    if (!newMessage.trim()) return;
    const newConv = {
      id: `conv_${Date.now()}`,
      title: newMessage.slice(0, 30) + (newMessage.length > 30 ? '...' : ''),
      lastMessage: '방금 전'
    };
    const updatedConversations = [newConv, ...(project.conversations || [])];
    updateProject(project.id, { conversations: updatedConversations });
    setProject({ ...project, conversations: updatedConversations });
    setActiveConversationId(newConv.id);
    setNewMessage('');
  };

  const handleFileUpload = () => {
    alert('파일 업로드 기능은 추후 구현 예정입니다.');
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-sedaily-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <FolderOpen className="w-16 h-16 text-text-500 mb-4" />
        <h2 className="text-xl font-semibold text-text-200 mb-2">프로젝트를 찾을 수 없습니다</h2>
        <button
          onClick={() => navigate('/projects')}
          className="text-sedaily-500 hover:text-sedaily-400"
        >
          프로젝트 목록으로 돌아가기
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex">
      {/* 메인 컨텐츠 (왼쪽) */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* 헤더 */}
        <div className="px-8 py-6">
          <button
            onClick={() => navigate('/projects')}
            className="flex items-center gap-2 text-sm text-text-400 hover:text-text-200 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            All projects
          </button>

          <div className="flex items-center justify-between">
            {isEditingName ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="text-2xl font-semibold bg-transparent text-text-100 outline-none border-b-2 border-sedaily-500"
                  autoFocus
                  onBlur={handleSaveName}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                />
              </div>
            ) : (
              <h1
                onClick={() => setIsEditingName(true)}
                className="text-2xl font-semibold text-text-100 cursor-pointer hover:text-text-200"
              >
                @{project.name}
              </h1>
            )}

            <div className="flex items-center gap-1">
              <button className="p-2 rounded-lg hover:bg-bg-200 text-text-400 hover:text-text-200 transition-colors">
                <MoreHorizontal className="w-5 h-5" />
              </button>
              <button className="p-2 rounded-lg hover:bg-bg-200 text-text-400 hover:text-text-200 transition-colors">
                <Star className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* 입력 영역 */}
        <div className="px-8 mb-6">
          <div className="bg-bg-200 rounded-2xl p-4 border border-bg-300">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Reply..."
              rows={3}
              className="w-full bg-transparent text-text-100 placeholder:text-text-500 outline-none resize-none text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleNewConversation();
                }
              }}
            />
            <div className="flex items-center justify-between mt-2">
              <button className="p-2 rounded-lg hover:bg-bg-300 text-text-400">
                <Plus className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-xs text-text-500">
                  <span>Opus 4.6</span>
                  <ChevronDown className="w-3 h-3" />
                </div>
                <button
                  onClick={handleNewConversation}
                  disabled={!newMessage.trim()}
                  className="p-2 rounded-lg text-text-400 hover:text-text-200 disabled:opacity-30"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 대화 목록 */}
        <div className="flex-1 overflow-y-auto px-8 pb-6">
          {project.conversations?.length > 0 ? (
            <div className="space-y-1">
              {project.conversations.map((conv) => (
                <ConversationItem
                  key={conv.id}
                  conversation={conv}
                  isActive={conv.id === activeConversationId}
                  onClick={() => navigate(`/chat/${conv.id}`)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-text-500">
              <p className="text-sm">아직 대화가 없습니다</p>
            </div>
          )}
        </div>
      </div>

      {/* 오른쪽 패널 */}
      <div className="w-80 border-l border-bg-300 p-6 space-y-8 overflow-y-auto">
        {/* Instructions */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-text-200 flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-amber-400" />
              Instructions
            </h3>
            <button className="p-1 rounded hover:bg-bg-200 text-text-400">
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {isEditingInstructions ? (
            <div>
              <textarea
                value={editInstructions}
                onChange={(e) => setEditInstructions(e.target.value)}
                placeholder="AI가 따라야 할 지침을 입력하세요..."
                rows={4}
                className="w-full bg-bg-200 text-text-100 text-sm px-3 py-2 rounded-lg outline-none resize-none border border-bg-300"
                autoFocus
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleSaveInstructions}
                  className="px-3 py-1 text-xs bg-sedaily-500 text-white rounded-lg"
                >
                  저장
                </button>
                <button
                  onClick={() => setIsEditingInstructions(false)}
                  className="px-3 py-1 text-xs text-text-400"
                >
                  취소
                </button>
              </div>
            </div>
          ) : (
            <p
              onClick={() => setIsEditingInstructions(true)}
              className="text-sm text-text-500 cursor-pointer hover:text-text-400"
            >
              {project.instructions || "Add instructions to tailor Claude's responses"}
            </p>
          )}
        </div>

        {/* Files */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-text-200 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Files
            </h3>
            <button
              onClick={handleFileUpload}
              className="p-1 rounded hover:bg-bg-200 text-text-400"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {project.files?.length > 0 ? (
            <div className="space-y-2">
              {project.files.map((file) => (
                <div key={file.id} className="flex items-center gap-2 p-2 bg-bg-200 rounded-lg">
                  <FileText className="w-4 h-4 text-text-400" />
                  <span className="text-sm text-text-200 truncate">{file.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <div
              onClick={handleFileUpload}
              className="border border-dashed border-bg-300 rounded-xl p-8 text-center cursor-pointer hover:border-text-500 transition-colors"
            >
              <div className="flex justify-center gap-4 mb-3">
                <div className="w-12 h-14 bg-bg-300 rounded-lg flex items-center justify-center relative">
                  <FileText className="w-6 h-6 text-text-500" />
                </div>
                <div className="w-12 h-14 bg-bg-300 rounded-lg flex items-center justify-center relative">
                  <FileText className="w-6 h-6 text-text-500" />
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-bg-200 rounded-full flex items-center justify-center border border-bg-300">
                    <Plus className="w-3 h-3 text-text-400" />
                  </div>
                </div>
              </div>
              <p className="text-xs text-text-500">
                Add PDFs, documents, or other text to<br />reference in this project.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
